import React, {useState, useEffect} from "react";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  IconButton,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { getAllPreSeasonActivitiesAction ,createMonthlyTargetAction,clearCreateMonthlyTargetAction } from "../../../store/actions";
import PropTypes from "prop-types";


const validationSchema = Yup.object({
  pre_target_id: Yup.string().required("Pre Activity is required"),
  month_year: Yup.string()
    .matches(/^\d{4}-(0[1-9]|1[0-2])$/, "Month must be in YYYY-MM format")
    .required("Month is required"),
  vlm_count: Yup.number()
    .typeError("VLM count must be a number")
    .integer("VLM count must be an integer")
    .min(0, "VLM count must be positive")
    .required("VLM count is required"),
  mvlm_count: Yup.number()
    .typeError("MVLM count must be a number")
    .integer("MVLM count must be an integer")
    .min(0, "MVLM count must be positive")
    .required("MVLM count is required"),
});

const MonthlyTarget = ({ open, onClose, onSubmit, preTargetId }) => {
  const dispatch = useDispatch();
  const [entries, setEntries] = useState([]);
  const { successData: preSeasonActivitiesData } = useSelector((state) => state.getAllPreSeasonActivitiesReducer || {});

  // Normalization helper for dropdown (flat array of pre-season targets)
  const normalizePreSeasonTargets = (rawData) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    const flatRows = [];
    const seenIds = new Set();

    // Flatten all valid pre-season targets (district and mandal)
    rawData.forEach((entry) => {
      if (Array.isArray(entry.targets)) {
        entry.targets.forEach((target) => {
          // Add district-level target
          if (target.id && !seenIds.has(target.id)) {
            flatRows.push({
              ...target,
              season: target.season,
              target_area_type: target.target_area_type,
              target_area_id: target.target_area_id,
            });
            seenIds.add(target.id);
          }
          // Add mandal-level pre_targets
          if (Array.isArray(target.mandals)) {
            target.mandals.forEach((mandalObj) => {
              if (Array.isArray(mandalObj.pre_targets)) {
                mandalObj.pre_targets.forEach((preTarget) => {
                  if (preTarget.id && !seenIds.has(preTarget.id)) {
                    flatRows.push({
                      ...preTarget,
                      season: preTarget.season,
                      target_area_type: preTarget.target_area_type,
                      target_area_id: preTarget.target_area_id,
                    });
                    seenIds.add(preTarget.id);
                  }
                });
              }
            });
          }
        });
      }
    });
    return flatRows;
  };

  const preSeasonActivitiesList = normalizePreSeasonTargets(preSeasonActivitiesData?.data);
  const { successData: createSuccessData, loading: createLoading, error: createError } = useSelector(
    (state) => state.createMonthlyTargetReducer || {}
  );

  const parseCountValue = (value) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) return null;
    return parsed;
  };

  const formik = useFormik({
    initialValues: {
      pre_target_id: "",
      month_year: "",
      vlm_count: "",
      mvlm_count: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {

      if (!values.pre_target_id) {
        toast.error("Please select a Pre-Season Activity first");
        return;
      }

      if (entries.some((entry) => entry.month_year === values.month_year)) {
        toast.error("This month is already added. Please choose a different month.");
        return;
      }

      // Get remaining counts for validation
      const selectedTarget = preSeasonActivitiesList.find(p => p.id === Number(values.pre_target_id));
      if (!selectedTarget) {
        toast.error("Invalid pre-season target selected");
        return;
      }
      
      const totalVlm = selectedTarget.vlm_count || 0;
      const totalMvlm = selectedTarget.mvlm_count || 0;
      
      const usedVlm = entries.reduce((sum, e) => sum + (e.vlm_count || 0), 0);
      const usedMvlm = entries.reduce((sum, e) => sum + (e.mvlm_count || 0), 0);
      
      const remainingVlm = totalVlm - usedVlm;
      const remainingMvlm = totalMvlm - usedMvlm;
      
      // Validate against remaining counts
      const vlmValue = parseCountValue(values.vlm_count);
      const mvlmValue = parseCountValue(values.mvlm_count);

      if (vlmValue === null || mvlmValue === null) {
        toast.error("VLM count and MVLM count must be non-negative integers");
        return;
      }
      
      if (vlmValue > remainingVlm) {
        toast.error(`VLM count exceeds remaining count. Remaining: ${remainingVlm} (Total: ${totalVlm}, Used: ${usedVlm})`);
        return;
      }
      if (mvlmValue > remainingMvlm) {
        toast.error(`MVLM count exceeds remaining count. Remaining: ${remainingMvlm} (Total: ${totalMvlm}, Used: ${usedMvlm})`);
        return;
      }

      // Add entry to the list with pre_target_id
      const newEntry = {
        pre_target_id: Number(values.pre_target_id),
        month_year: values.month_year,
        vlm_count: vlmValue,
        mvlm_count: mvlmValue,
      };
      
      
      setEntries((prev) => {
        const updated = [...prev, newEntry];
        return updated;
      });
      
      // Reset only the entry fields, keep pre_target_id
      formik.setFieldValue("month_year", "");
      formik.setFieldValue("vlm_count", "");
      formik.setFieldValue("mvlm_count", "");
      toast.success("Entry added successfully!");
    },
  });

  // Get the selected pre target details to access counts (after formik is initialized)
  const selectedPreTarget = React.useMemo(() => {
    return preSeasonActivitiesList.find(p => p.id === Number(formik.values.pre_target_id));
  }, [preSeasonActivitiesList, formik.values.pre_target_id]);
  
  // Calculate remaining allowance for each field
  const remainingCounts = React.useMemo(() => {
    if (!selectedPreTarget) {
      return { vlm: 0, mvlm: 0, totalVlm: 0, totalMvlm: 0, usedVlm: 0, usedMvlm: 0 };
    }
    
    const totalVlm = selectedPreTarget.vlm_count || 0;
    const totalMvlm = selectedPreTarget.mvlm_count || 0;
    
    // Sum up already added entries
    const usedVlm = entries.reduce((sum, e) => sum + (e.vlm_count || 0), 0);
    const usedMvlm = entries.reduce((sum, e) => sum + (e.mvlm_count || 0), 0);
    
    return {
      vlm: totalVlm - usedVlm,
      mvlm: totalMvlm - usedMvlm,
      totalVlm,
      totalMvlm,
      usedVlm,
      usedMvlm
    };
  }, [selectedPreTarget, entries]);

  // Reset form when modal is closed; pre-fill pre_target_id when opened with a known id
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setEntries([]);
    } else if (preTargetId) {
      formik.setFieldValue('pre_target_id', preTargetId);
    }
  }, [open, preTargetId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitAll = async () => {
    if (!formik.values.pre_target_id) {
      toast.error("Please select a Pre-Season Activity");
      return;
    }

    if (entries.length === 0) {
      toast.error("Please add at least one monthly target entry");
      return;
    }

    const monthSet = new Set();
    for (const item of entries) {
      if (!item.month_year || monthSet.has(item.month_year)) {
        toast.error("Duplicate or invalid month found in entries. Please keep months unique.");
        return;
      }
      monthSet.add(item.month_year);
    }

    const selectedTarget = preSeasonActivitiesList.find((p) => p.id === Number(formik.values.pre_target_id));
    if (!selectedTarget) {
      toast.error("Selected Pre-Season Activity is invalid");
      return;
    }

    const totalVlm = selectedTarget.vlm_count || 0;
    const totalMvlm = selectedTarget.mvlm_count || 0;
    const usedVlm = entries.reduce((sum, e) => sum + (e.vlm_count || 0), 0);
    const usedMvlm = entries.reduce((sum, e) => sum + (e.mvlm_count || 0), 0);

    if (usedVlm > totalVlm || usedMvlm > totalMvlm) {
      toast.error(`Total monthly allocation exceeds pre target limit. VLM: ${usedVlm}/${totalVlm}, MVLM: ${usedMvlm}/${totalMvlm}`);
      return;
    }

    // Each item already has pre_target_id, send only items array
    const payload = {
      items: entries,
    };


    
    dispatch(createMonthlyTargetAction(payload));
  };

  const addEntry = () => {
    formik.handleSubmit();
  };

  useEffect(() => {
    dispatch(getAllPreSeasonActivitiesAction());
  }, [dispatch]);

  useEffect(() => {
    if (createSuccessData) {
      toast.success("Monthly target updated successfully!");
      dispatch(clearCreateMonthlyTargetAction());
      setEntries([]);
      formik.resetForm();
      onSubmit?.(createSuccessData);
      onClose?.();
    }
  }, [createSuccessData, dispatch, onSubmit, onClose]);

  useEffect(() => {
    if (createError) {
      toast.error(createError?.message || "Failed to create monthly target");
      dispatch(clearCreateMonthlyTargetAction());
    }
  }, [createError]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600, fontSize: 22 }}>
        Monthly pre Season Update
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <FormControl 
            fullWidth 
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 3 },
              ...(preTargetId ? {
                "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: '#F3F4F6' },
                "& .MuiInputBase-input": { color: 'rgba(0,0,0,0.87)', WebkitTextFillColor: 'rgba(0,0,0,0.87)' },
                "& .MuiInputLabel-root": { color: 'rgba(0,0,0,0.6)' },
              } : {}),
            }}
            error={formik.touched.pre_target_id && Boolean(formik.errors.pre_target_id)}
          >
            <InputLabel id="preactivity-select-label">Pre-Season Activity</InputLabel>
            <Select
              labelId="preactivity-select-label"
              name="pre_target_id"
              value={formik.values.pre_target_id}
              label="Pre-Season Activity"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={preTargetId ? { readOnly: true } : {}}
              renderValue={(val) => {
                const found = preSeasonActivitiesList.find(p => String(p.id) === String(val));
                if (!found) return val ? `ID: ${val}` : '';
                return `ID: ${found.id} - ${found.season?.name} (${found.season?.year}) - ${found.target_area_type}`;
              }}
            >
              {preSeasonActivitiesList.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  ID: {p.id} - {p.season?.name} ({p.season?.year}) - {p.target_area_type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.pre_target_id && formik.errors.pre_target_id && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formik.errors.pre_target_id}
              </Typography>
            )}
          </FormControl>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr auto' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Month (YYYY-MM)
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year', 'month']}
                  label="Select Month and Year"
                  minDate={dayjs('2020-01-01')}
                  maxDate={dayjs('2099-12-31')}
                  value={formik.values.month_year ? dayjs(formik.values.month_year) : null}
                  onChange={value => {
                    formik.setFieldValue('month_year', value ? value.format('YYYY-MM') : '');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: formik.touched.month_year && Boolean(formik.errors.month_year),
                      helperText: formik.touched.month_year && formik.errors.month_year,
                      sx: {
                        "& .MuiOutlinedInput-root": { borderRadius: 3 },
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                VLM Count
              </Typography>
              <TextField
                fullWidth
                name="vlm_count"
                type="number"
                placeholder="0"
                value={formik.values.vlm_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.vlm_count && Boolean(formik.errors.vlm_count)}
                helperText={
                  formik.touched.vlm_count && formik.errors.vlm_count 
                    ? formik.errors.vlm_count 
                    : selectedPreTarget 
                      ? `Max: ${remainingCounts.vlm}` 
                      : ''
                }
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    appearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                MVLM Count
              </Typography>
              <TextField
                fullWidth
                name="mvlm_count"
                type="number"
                placeholder="0"
                value={formik.values.mvlm_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.mvlm_count && Boolean(formik.errors.mvlm_count)}
                helperText={
                  formik.touched.mvlm_count && formik.errors.mvlm_count 
                    ? formik.errors.mvlm_count 
                    : selectedPreTarget 
                      ? `Max: ${remainingCounts.mvlm}` 
                      : ''
                }
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    appearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", pt: 2.75 }}>
              <Button 
                variant="outlined" 
                onClick={addEntry}
                type="button"
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  minWidth: '80px',
                  fontWeight: 600
                }}
              >
                ADD
              </Button>
            </Box>
          </Box>
        </form>

        {entries.length > 0 && (
          <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, border: "1px solid #E5E7EB" }}>
            {/* Desktop table */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Pre Target ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Month (YYYY-MM)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>VLM Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>MVLM Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={`${e.pre_target_id}-${e.month_year}`}>
                      <TableCell>{e.pre_target_id || "N/A"}</TableCell>
                      <TableCell>{e.month_year}</TableCell>
                      <TableCell>{e.vlm_count}</TableCell>
                      <TableCell>{e.mvlm_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Mobile card list */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              {entries.map((e, idx) => (
                <Box
                  key={`${e.pre_target_id}-${e.month_year}`}
                  sx={{
                    p: 2,
                    borderBottom: idx < entries.length - 1 ? '1px solid #E5E7EB' : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Pre Target ID</Typography>
                    <Typography variant="caption">{e.pre_target_id || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Month</Typography>
                    <Typography variant="caption">{e.month_year}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>VLM Count</Typography>
                    <Typography variant="caption">{e.vlm_count}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>MVLM Count</Typography>
                    <Typography variant="caption">{e.mvlm_count}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: 3, px: 4, py: 1.25, boxShadow: "0 6px 16px rgba(37,99,235,0.35)" }}
          onClick={handleSubmitAll}
          disabled={createLoading || entries.length === 0}
        >
          {createLoading ? "Submitting..." : "Submit Data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MonthlyTarget.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  preTargetId: PropTypes.number,
};

export default MonthlyTarget;